import React, { useState, useEffect, useCallback } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import { API_URI } from "../../helpers";
import AgenteAnalisis from "./AgenteAnalisis.js";
import Rendimiento from "./Rendimiento.js";

// ---- helpers ----
const fmt0 = (n) => (n == null ? "—" : Math.round(n).toLocaleString("es-AR"));
const money = (n) => (n == null ? "—" : "$" + fmt0(n));
/*
 * Fechas del día COMERCIAL, en horario de Argentina.
 *
 * Acá NO se puede usar `toISOString()`, que es lo que hacía antes: convierte a UTC, y a
 * partir de las 21:00 de Argentina UTC ya está en el día siguiente. Entre las 21:00 y la
 * medianoche la pantalla pedía un día que todavía no había empezado y devolvía todo en cero
 * — se lee como que Insights dejó de funcionar, cuando en realidad los datos estaban bien.
 *
 * Se fija el huso en vez de usar el del navegador porque el día que hay que cortar es el de
 * las ventas, que se guardan en -03: mirar el panel desde otro huso no cambia cuál es "hoy"
 * para el negocio.
 */
const ZONA = "America/Argentina/Buenos_Aires";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: ZONA });

function isoDaysAgo(n) {
  // Se resta sobre el mediodía UTC del día comercial: a esa hora ningún corrimiento de huso
  // llega a cruzar la medianoche, así que restar días no puede caer en el día equivocado.
  const d = new Date(today() + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().substring(0, 10);
}

function Delta({ cur, prev, pct, invert }) {
  if (prev == null || prev === 0) return null;
  const diff = pct ? cur - prev : ((cur - prev) / Math.abs(prev)) * 100;
  const up = diff >= 0;
  const good = invert ? !up : up;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: good ? "#16A34A" : "#DC2626", marginLeft: 6 }}>
      {up ? "▲" : "▼"} {Math.abs(diff).toFixed(pct ? 1 : 0)}{pct ? "pp" : "%"}
    </span>
  );
}

function Metric({ label, value, cur, prev, pct, invert, color }) {
  return (
    <Card style={{ marginTop: 0 }}>
      <CardBody style={{ textAlign: "center", padding: "16px 8px" }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: color || "#1f2937" }}>{value}</div>
        <div style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
          {label}<Delta cur={cur} prev={prev} pct={pct} invert={invert} />
        </div>
      </CardBody>
    </Card>
  );
}

const PRIO_COLOR = { high: "#DC2626", med: "#CA8A04", low: "#94A3B8" };
const ACTION_ES = { subir: "SUBIR", pausar: "PAUSAR", recortar: "RECORTAR", escalar: "ESCALAR", revisar: "REVISAR", canal: "CANAL", error: "—" };

const PRESETS = [
  { key: "today", label: "Hoy", from: today(), to: today() },
  { key: "yesterday", label: "Ayer", from: isoDaysAgo(1), to: isoDaysAgo(1) },
  { key: "7d", label: "7 días", from: isoDaysAgo(6), to: today() },
  { key: "14d", label: "14 días", from: isoDaysAgo(13), to: today() },
  { key: "30d", label: "30 días", from: isoDaysAgo(29), to: today() },
];

export default function Insights() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [preset, setPreset] = useState("today");
  const [tab, setTab] = useState(0);

  // Default "Hoy" → con corte por hora activado (comparación justa vs ayer mismo horario).
  const [cutoff, setCutoff] = useState(true);
  const [ov, setOv] = useState(null);
  const [meta, setMeta] = useState(null);
  const [google, setGoogle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sug, setSug] = useState(null);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugDays, setSugDays] = useState(14); // ventana propia p/ sugerencias de anuncios

  const fetchOverview = useCallback(() => {
    setLoading(true);
    fetch(`${API_URI}/dashboard/insights?from=${from}&to=${to}${cutoff ? "&cutoff=1" : ""}`)
      .then((r) => r.json()).then(setOv).finally(() => setLoading(false));
  }, [from, to, cutoff]);

  // Las sugerencias de anuncios usan SU PROPIA ventana (default 14d), independiente
  // del período del overview: un solo día no alcanza para decidir cambios de campañas.
  const fetchSuggestions = useCallback((days) => {
    const d = days || sugDays;
    setSugDays(d); setSugLoading(true); setSug(null);
    const f = isoDaysAgo(d - 1);
    fetch(`${API_URI}/dashboard/insights/suggestions?from=${f}&to=${today()}`)
      .then((r) => r.json()).then(setSug).finally(() => setSugLoading(false));
  }, [sugDays]);

  const fetchMeta = useCallback(() => {
    setMeta(null);
    fetch(`${API_URI}/dashboard/insights/meta?from=${from}&to=${to}`).then((r) => r.json()).then(setMeta);
  }, [from, to]);

  const fetchGoogle = useCallback(() => {
    setGoogle(null);
    fetch(`${API_URI}/dashboard/insights/google?from=${from}&to=${to}`).then((r) => r.json()).then(setGoogle);
  }, [from, to]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => {
    if (tab === 1) fetchMeta();
    if (tab === 2) fetchGoogle();
  }, [tab, fetchMeta, fetchGoogle]);

  function applyPreset(p) {
    setPreset(p.key); setFrom(p.from); setTo(p.to);
    // "Hoy" es parcial → auto-activa el corte por hora para comparar justo.
    // Cualquier otro rango lo desactiva (ahí los días completos sirven así).
    setCutoff(p.key === "today");
  }

  const t = ov && ov.totals;
  const c = ov && ov.compare;

  return (
    <GridContainer>
      {/* Agente de análisis: capa flotante, fuera de la grilla. Es una burbuja arrastrable
          encima de la pantalla — misma criatura que la del Centro de Mensajes. */}
      <AgenteAnalisis />

      {/* Selector de período */}
      <GridItem xs={12}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm" color={preset === p.key ? "info" : "white"} onClick={() => applyPreset(p)}>{p.label}</Button>
          ))}
          <span style={{ marginLeft: 8 }}>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} style={{ padding: 4 }} />
            &nbsp;→&nbsp;
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} style={{ padding: 4 }} />
          </span>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }} title="Recorta cada día al horario de ahora para comparar justo (gasto prorrateado).">
            <input type="checkbox" checked={cutoff} onChange={(e) => setCutoff(e.target.checked)} />
            hasta la hora actual{ov && ov.cutoff ? ` (${ov.cutoff})` : ""}
          </label>
          <Button size="sm" color="info" onClick={fetchOverview}>Actualizar</Button>
          <Button size="sm" color="rose" onClick={() => fetchSuggestions()}>💡 Sugerencias (14d)</Button>
          {ov && ov.prev && <span style={{ fontSize: 12, color: "#94A3B8" }}>comparando vs {ov.prev.from} → {ov.prev.to}</span>}
        </div>

        {/* Panel de sugerencias */}
        {(sugLoading || sug) && (
          <Card>
            <CardHeader color="rose">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ color: "#fff" }}>Sugerencias para escalar (Meta + Google)</span>
                <span style={{ flex: 1 }} />
                <span style={{ color: "rgba(255,255,255,.85)", fontSize: 12 }}>
                  ventana:{sug && sug.range ? ` ${sug.range.from} → ${sug.range.to}` : ""}
                </span>
                {[7, 14, 30].map((d) => (
                  <Button key={d} size="sm" color={sugDays === d ? "white" : "rose"} onClick={() => fetchSuggestions(d)}>{d}d</Button>
                ))}
              </div>
            </CardHeader>
            <CardBody>
              {sugLoading && <div>Analizando campañas…</div>}
              {sug && !sug.result && <div style={{ color: "#DC2626" }}>No se pudieron generar sugerencias.</div>}
              {sug && sug.result && (
                <>
                  {sug.context && (
                    <div style={{ fontSize: 13, color: "#64748B", marginBottom: 8 }}>
                      Negocio: ROAS {sug.context.business_roas} · Neto {money(sug.context.net)} · Meta {money(sug.context.meta_spend)} / Google {money(sug.context.google_spend)}
                    </div>
                  )}
                  {sug.suggestions.length === 0 && <div style={{ color: "#94A3B8" }}>Sin ajustes sugeridos para el período.</div>}
                  {sug.suggestions.map((x, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: x.channel === "meta" ? "#2563EB" : "#16A34A", borderRadius: 4, padding: "2px 6px", minWidth: 52, textAlign: "center" }}>{x.channel}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: PRIO_COLOR[x.priority] || "#64748B", textTransform: "uppercase", minWidth: 62 }}>{ACTION_ES[x.action] || x.action}</span>
                      <div style={{ flex: 1, fontSize: 13 }}>
                        <strong>{x.target}</strong> — {x.detail}
                      </div>
                    </div>
                  ))}
                  {sug.note && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 10 }}>ℹ️ {sug.note}</div>}
                </>
              )}
            </CardBody>
          </Card>
        )}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Resumen" /><Tab label="Meta" /><Tab label="Google" />
        </Tabs>
      </GridItem>

      {/* ---------- RESUMEN ---------- */}
      {tab === 0 && t && (
        <>
          {/* Siete tarjetas: 4 + 3 en escritorio. La de Rendimiento va primera porque es la
              única que compara contra el mismo día de la semana y el mismo mes del año, y
              es la que se abre en detalle. */}
          <GridItem xs={12} sm={6} md={3}><Rendimiento /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="Ventas" value={fmt0(t.sales)} cur={t.sales} prev={c && c.sales} color="#1f2937" /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="ROAS" value={t.roas} cur={t.roas} prev={c && c.roas} color="#16A34A" /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="Neto" value={money(t.net)} cur={t.net} prev={c && c.net} color={t.net >= 0 ? "#16A34A" : "#DC2626"} /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="AOV" value={money(t.aov)} cur={t.aov} prev={c && c.aov} /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="% 5 años" value={t.pct5 + "%"} cur={t.pct5} prev={c && c.pct5} pct /></GridItem>
          <GridItem xs={6} sm={4} md={3}><Metric label="Conversión checkout" value={t.conv + "%"} cur={t.conv} prev={c && c.conv} pct /></GridItem>

          <GridItem xs={12}>
            <div style={{ fontSize: 13, color: "#64748B", margin: "4px 0" }}>
              Facturado {money(t.rev)} · Gasto {money(t.spend)} (Meta {money(t.meta_spend)} / Google {money(t.google_spend)}) · Carritos {t.carr}
            </div>
          </GridItem>

          {/* Tabla por día */}
          <GridItem xs={12} md={7}>
            <Card><CardHeader color="info"><span style={{ color: "#fff" }}>Rendimiento por día</span></CardHeader>
              <CardBody>
                <Table size="small">
                  <TableHead><TableRow>
                    {["Fecha", "Real", "Vtas", "Conv%", "A/5", "Carr", "Rev", "Gasto", "ROAS", "Neto"].map((h) => <TableCell key={h} style={{ fontWeight: 600, padding: "6px 8px" }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {(ov.daily || []).map((r) => (
                      <TableRow key={r.date}>
                        <TableCell style={{ padding: "6px 8px" }}>{r.date.substring(5)}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{r.real}<span style={{ color: "#CBD5E1", fontSize: 11 }}> /{r.intent}</span></TableCell>
                        <TableCell style={{ padding: "6px 8px", fontWeight: 600 }}>{r.sales}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{r.conv}%</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{r.anual}/{r.cinco}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{r.carr}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{money(r.rev)}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{money(r.spend)}</TableCell>
                        <TableCell style={{ padding: "6px 8px", fontWeight: 600, color: r.roas >= 2 ? "#16A34A" : r.roas >= 1 ? "#CA8A04" : "#DC2626" }}>{r.roas}</TableCell>
                        <TableCell style={{ padding: "6px 8px", color: r.net >= 0 ? "#16A34A" : "#DC2626" }}>{money(r.net)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardBody></Card>
          </GridItem>

          {/* Tabla por país */}
          <GridItem xs={12} md={5}>
            <Card><CardHeader color="info"><span style={{ color: "#fff" }}>Por país</span></CardHeader>
              <CardBody>
                <Table size="small">
                  <TableHead><TableRow>
                    {["País", "Vtas", "Rev", "Gasto", "ROAS", "Neto"].map((h) => <TableCell key={h} style={{ fontWeight: 600, padding: "6px 8px" }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {(ov.countries || []).map((r) => (
                      <TableRow key={r.country}>
                        <TableCell style={{ padding: "6px 8px" }}>{r.country}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{r.sales}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{money(r.rev)}</TableCell>
                        <TableCell style={{ padding: "6px 8px" }}>{money(r.spend)}</TableCell>
                        <TableCell style={{ padding: "6px 8px", fontWeight: 600, color: r.roas == null ? "#94A3B8" : r.roas >= 2 ? "#16A34A" : r.roas >= 1 ? "#CA8A04" : "#DC2626" }}>{r.roas == null ? "—" : r.roas}</TableCell>
                        <TableCell style={{ padding: "6px 8px", color: r.net >= 0 ? "#16A34A" : "#DC2626" }}>{money(r.net)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardBody></Card>
          </GridItem>

          {(ov.today || []).length > 0 && (
            <GridItem xs={12}>
              <Card><CardHeader color="success"><span style={{ color: "#fff" }}>Ventas de hoy</span></CardHeader>
                <CardBody>
                  {ov.today.map((s, i) => (
                    <div key={i} style={{ fontSize: 13, padding: "2px 0" }}>
                      {s.time.substring(11, 16)} · {s.channel} · <strong>{s.plan}</strong> · {money(s.usd)}
                    </div>
                  ))}
                </CardBody></Card>
            </GridItem>
          )}
        </>
      )}
      {tab === 0 && !t && <GridItem xs={12}><div style={{ padding: 24 }}>{loading ? "Cargando…" : "Sin datos."}</div></GridItem>}

      {/* ---------- META ---------- */}
      {tab === 1 && <GridItem xs={12}>{renderAdTable(meta, "Meta", ["Campaña", "Gasto", "CTR", "Compras", "Valor", "ROAS", "CPA"],
        (r) => [r.name, money(r.spend), r.ctr + "%", r.purchases, money(r.value), r.roas, r.cpa == null ? "—" : money(r.cpa)],
        (tot) => `Gasto ${money(tot.spend)} · ${tot.purchases} compras · ROAS ${tot.roas} · CPA ${tot.cpa == null ? "—" : money(tot.cpa)}`)}</GridItem>}

      {/* ---------- GOOGLE ---------- */}
      {tab === 2 && <GridItem xs={12}>{renderAdTable(google, "Google", ["Campaña", "Gasto", "Conv.", "CPA", "Clicks", "Impr."],
        (r) => [r.name, money(r.spend), r.conversions, r.cpa == null ? "—" : money(r.cpa), fmt0(r.clicks), fmt0(r.impressions)],
        (tot) => `Gasto ${money(tot.spend)} · ${tot.conversions} conv · CPA ${tot.cpa == null ? "—" : money(tot.cpa)}`)}</GridItem>}
    </GridContainer>
  );
}

function renderAdTable(payload, title, headers, rowFn, totFn) {
  if (!payload) return <div style={{ padding: 24 }}>Cargando {title}…</div>;
  if (!payload.result) return <div style={{ padding: 24, color: "#DC2626" }}>No se pudo cargar {title} ({payload.reason || "error"}).</div>;
  return (
    <Card>
      <CardHeader color="info">
        <span style={{ color: "#fff" }}>Campañas {title}</span>
        <div style={{ color: "rgba(255,255,255,.8)", fontSize: 12 }}>{payload.totals ? totFn(payload.totals) : ""}</div>
      </CardHeader>
      <CardBody>
        <Table size="small">
          <TableHead><TableRow>{headers.map((h) => <TableCell key={h} style={{ fontWeight: 600 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {(payload.campaigns || []).length === 0 && <TableRow><TableCell colSpan={headers.length} style={{ textAlign: "center", color: "#94A3B8" }}>Sin campañas con gasto en el período.</TableCell></TableRow>}
            {(payload.campaigns || []).map((r, i) => (
              <TableRow key={i}>{rowFn(r).map((cell, j) => <TableCell key={j} style={{ fontWeight: j === 0 ? 500 : 400 }}>{cell}</TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}
